import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Switch from 'react-switch';
import Color from 'color';

import useWindowDimensions from '../../hooks/useWindowDimensions';
import background from '../../images/background.jpg';
import StatusRow from '../StatusRow';
import LoseGame from '../LoseGame';

import type { BlocoMatrix, GameMap, Player, Status } from '../../types';

const Game = styled.div<{ portrait?: boolean }>`
	width: 100vw;
	height: ${(props) => (props.portrait ? '95' : '100')}vh;
	display: flex;
	flex-direction: ${(props) => (props.portrait ? 'column' : 'row')};
	justify-content: center;
	align-items: center;
	background-image: url(${background});
	background-size: cover;
	background-position: center;
`;

const ContainerNext = styled.div<{ portrait?: boolean; pixelSize: number }>`
	${(props) => !props.portrait && `height: ${props.pixelSize * 18 + (18 / 3) * 1}px;`}
	${(props) => props.portrait && `width: ${props.pixelSize * 10 + (10 / 3) * 1}px;`}
	margin-right: ${(props) => (props.portrait ? 0 : props.pixelSize / 3)}px;
	margin-bottom: ${(props) => (props.portrait ? props.pixelSize / 3 : 0)}px;
	display: flex;
	flex-direction: ${(props) => (props.portrait ? 'row' : 'column')};
	justify-content: ${(props) => (props.portrait ? 'space-between' : 'flex-start')};
	align-items: center;
`;

const Next = styled.div<{ portrait?: boolean; theme3d?: boolean; pixelSize: number }>`
	width: ${(props) => props.pixelSize * 3}px;
	height: ${(props) => props.pixelSize * 3}px;
	background-color: ${(props) => (props.theme3d ? '#444' : 'black')};
	transition: background-color 0.5;
	border: ${(props) => props.pixelSize / 10}px solid white;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin-bottom: ${(props) => (props.portrait ? 0 : props.pixelSize / 3)}px;
	margin-right: ${(props) => (!props.portrait ? 0 : props.pixelSize / 3)}px;
`;

const StyledStage = styled.div<{ theme3d?: boolean; pixelSize: number }>`
	border: ${(props) => props.pixelSize / 10}px solid white;
	background-color: ${(props) => (props.theme3d ? '#444' : 'black')};
	transition: background-color 0.5s;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const Center = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Row = styled.div<{ stage?: boolean | string; pixelSize: number }>`
	display: flex;
	flex-direction: row;
	justify-content: center;
	height: ${(props) => (props.stage ? props.pixelSize : props.pixelSize / 1.6)}px;
`;

interface PixelProps {
  stage?: boolean | string;
  pixelSize: number;
  fill?: number;
  color?: string | string[];
  paused?: boolean;
  theme3d?: boolean | string;
  topBloco?: boolean | number | string;
  zIndex?: number;
  hint?: boolean;
  playerColor?: string;
}

const Pixel = React.memo(styled.div<PixelProps>`
	width: ${(props) => (props.stage ? props.pixelSize : props.pixelSize / 1.6)}px;
	height: ${(props) => (props.stage ? props.pixelSize : props.pixelSize / 1.6)}px;
	background-color: ${(props) => (props.fill === 1 ? props.color : 'inherited')};
	position: relative;
	z-index: ${(props) => props.zIndex};

	${(props) =>
    props.paused &&
    `
		transition: all 1s;
	`};

	${(props) =>
    props.fill &&
    props.theme3d &&
    `;
		box-shadow: ${props.pixelSize / 4.16}px ${
      props.pixelSize / 4.16
    }px ${props.pixelSize / 5.55}px #222${
      props.topBloco
        ? `, 0 ${-props.pixelSize / 4.16}px 0 ${Color(props.color).lighten(0.2)}`
        : ''
    }
	`};

	${(props) =>
    !props.theme3d &&
    `
		border-left: 1px solid ${props.stage || props.fill || props.hint ? '#222' : 'black'};
		border-top: 1px solid ${props.stage || props.fill || props.hint ? '#222' : 'black'};
	`};

	${(props) =>
    props.hint &&
    `
		border: 1px solid ${Color(props.playerColor).alpha(0.5)};
		background-color: rgba(255,255,255,0.1);
	`};
`);

const ContainerSwitch = styled.div<{ portrait?: boolean }>`
	${(props) =>
    props.portrait &&
    `
		height: 100%;
		width: 100%;
		display: flex;
		align-items: flex-end;
		flex-direction: column;
		justify-content: flex-end;
	`};
`;

const ContainerStatus = styled.div<{ portrait?: boolean; pixelSize: number }>`
	width: ${(props) => props.pixelSize * 8}px;
	${(props) => !props.portrait && `height: ${props.pixelSize * 18 + (18 / 3) * 1}px;`}
	${(props) => props.portrait && `width: ${props.pixelSize * 10 + (10 / 3) * 1}px;`}
	margin-left: ${(props) => (props.portrait ? 0 : props.pixelSize / 3)}px;
	margin-top: ${(props) => (props.portrait ? props.pixelSize / 3 : 0)}px;
	display: flex;
	flex-direction: ${(props) => (props.portrait ? 'row' : 'column')};
	align-items: center;
	justify-content: ${(props) => (props.portrait ? 'space-between' : 'flex-start')};
	font-size: ${(props) => props.pixelSize}px;
`;

const getRenderizacaoBloco = (bloco: BlocoMatrix): BlocoMatrix => {
  const trimRowBloco: BlocoMatrix = [];
  const sumColumn: Record<number, number> = {};
  bloco.forEach((row) => {
    let rowSum = 0;
    row.forEach((pixel) => (rowSum = rowSum + pixel));
    if (rowSum > 0) trimRowBloco.push(row);
    row.forEach((pixel, x) => {
      sumColumn[x] = (sumColumn[x] ? sumColumn[x] : 0) + pixel;
    });
  });
  const trimBloco: BlocoMatrix = [];
  trimRowBloco.forEach((row) => {
    const newRow: number[] = [];
    row.forEach((pixel, x) => {
      if (sumColumn[x] > 0) newRow.push(pixel);
    });
    trimBloco.push(newRow);
  });
  return trimBloco;
};

interface StageProps {
  lose: boolean;
  restartClick: () => void;
  map: GameMap;
  player: Player;
  hint: Player;
  status: Status;
  paused: boolean;
  // onClick + the gesture handlers spread from Game's useDrag bind().
  [key: string]: unknown;
}

const Stage = ({
  lose,
  restartClick,
  map,
  player,
  hint,
  status,
  paused,
  ...others
}: StageProps) => {
  const [pixelSize, setPixelSize] = useState(30);
  const [portrait, setPortrait] = useState(false);
  const { width, height } = useWindowDimensions();
  const [theme3d, setTheme3d] = useState(false);
  const [nextRender, setNextRender] = useState<BlocoMatrix>();
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let pixelSizeHeight = height / 20;
    let pixelSizeWidth = width / 32;
    if (portrait) {
      pixelSizeHeight = height / 26;
      pixelSizeWidth = width / 12;
    }
    setPixelSize(pixelSizeWidth < pixelSizeHeight ? pixelSizeWidth : pixelSizeHeight);
    setPortrait(height > width);
  }, [width, height, portrait]);

  useEffect(() => {
    if (!player.next) return;
    setNextRender(getRenderizacaoBloco(player.next.bloco));
  }, [player.next]);

  useEffect(() => {
    if (!lose) {
      stageRef.current?.focus();
    }
  }, [lose]);

  useEffect(() => {
    stageRef.current?.focus();
  }, [theme3d]);

  return (
    <div>
      <Game portrait={portrait}>
        {nextRender && (
          <ContainerNext portrait={portrait} pixelSize={pixelSize}>
            <Next portrait={portrait} theme3d={theme3d} pixelSize={pixelSize}>
              {nextRender.map((row, y) => (
                <Row pixelSize={pixelSize} key={`row-${y}`}>
                  {row.map((pixel, x) => {
                    const topBloco =
                      pixel && (!nextRender[y - 1] || !nextRender[y - 1][x]);
                    return (
                      <Pixel
                        paused={paused}
                        theme3d={theme3d}
                        topBloco={topBloco}
                        zIndex={y}
                        pixelSize={pixelSize}
                        key={`pixel-${x}`}
                        fill={pixel}
                        color={player.next?.color}
                      />
                    );
                  })}
                </Row>
              ))}
            </Next>
            <ContainerSwitch portrait={portrait}>
              <Switch
                width={pixelSize * 2}
                height={pixelSize / 1.2}
                onChange={setTheme3d}
                checked={theme3d}
                offColor='#000'
                onColor='#444'
                uncheckedIcon={
                  <Center>
                    <Pixel
                      theme3d={false}
                      pixelSize={pixelSize / 3}
                      stage='true'
                      fill={1}
                      color={'#e54b4b'}
                    />
                  </Center>
                }
                checkedIcon={
                  <Center>
                    <Pixel
                      theme3d='true'
                      pixelSize={pixelSize / 3}
                      stage='true'
                      fill={1}
                      color={'#e54b4b'}
                      topBloco='true'
                    />
                  </Center>
                }
              />
            </ContainerSwitch>
          </ContainerNext>
        )}
        {map && (
          <StyledStage ref={stageRef} {...others} theme3d={theme3d} pixelSize={pixelSize}>
            {map.map((row, y) => (
              <Row stage='true' pixelSize={pixelSize} key={`row-${y}`}>
                {row.map((pixel, x) => {
                  const playerFill =
                    player.bloco.bloco[y - player.pos[0]] &&
                    player.bloco.bloco[y - player.pos[0]][x - player.pos[1]];
                  const playerHint =
                    hint.bloco.bloco[y - hint.pos[0]] &&
                    hint.bloco.bloco[y - hint.pos[0]][x - hint.pos[1]];
                  const topBloco =
                    (playerFill || pixel.fill) &&
                    (!player.bloco.bloco[y - player.pos[0] - 1] ||
                      !player.bloco.bloco[y - player.pos[0] - 1][x - player.pos[1]]) &&
                    (!map[y - 1] || !map[y - 1][x].fill);
                  const zIndex = !playerFill && !pixel.fill && playerHint ? 99 : y;
                  return (
                    <Pixel
                      paused={paused}
                      theme3d={theme3d}
                      hint={!pixel.fill && !playerFill && !!playerHint}
                      pixelSize={pixelSize}
                      stage='true'
                      key={`pixel-${x}`}
                      fill={pixel.fill || playerFill}
                      color={playerFill ? player.bloco.color : pixel.color}
                      playerColor={player.bloco.color}
                      topBloco={topBloco}
                      zIndex={zIndex}
                    ></Pixel>
                  );
                })}
              </Row>
            ))}
          </StyledStage>
        )}
        {status && (
          <ContainerStatus portrait={portrait} pixelSize={pixelSize}>
            <StatusRow
              backgroundColor={theme3d ? '#444' : 'black'}
              portrait={portrait}
              borderSize={pixelSize / 10}
              margin={pixelSize / 3}
              padding={pixelSize / 2}
              title='SCORE'
              value={status.score}
            />
            <StatusRow
              backgroundColor={theme3d ? '#444' : 'black'}
              portrait={portrait}
              borderSize={pixelSize / 10}
              margin={pixelSize / 3}
              padding={pixelSize / 2}
              title='LEVEL'
              value={status.level}
            />
            <StatusRow
              backgroundColor={theme3d ? '#444' : 'black'}
              portrait={portrait}
              borderSize={pixelSize / 10}
              margin={pixelSize / 3}
              padding={pixelSize / 2}
              title='LINES'
              value={status.lines}
            />
          </ContainerStatus>
        )}
      </Game>
      {lose && (
        <LoseGame
          portrait={portrait}
          restartClick={restartClick}
          status={status}
          pixelSize={pixelSize}
          theme3d={theme3d}
        />
      )}
    </div>
  );
};

export default Stage;
